import { useState } from 'react';
import { useQuery } from '../../hooks/useQuery';
import { api, type PlanVersion, type PlanFormulaRow } from '../../lib/api';
import { Spinner, ActionButton, Modal, Badge } from '../ui';
import { fmtMonth, fmtCurrencyWhole } from '../../lib/fmt';

/**
 * Planning v2 Formulas — custom P&L lines defined as expressions over named
 * references. Kept in the grid idiom: a clean row grid + a centered popup
 * editor that carries the reference palette and live preview (the two things
 * that make formula authoring workable), consistent with the other sections.
 */

const box: React.CSSProperties = { fontSize: 12, padding: '4px 7px', border: '0.5px solid var(--color-border-strong)', borderRadius: 'var(--radius-sm)', background: 'var(--color-surface)', color: 'var(--color-text)' };
const th: React.CSSProperties = { padding: '4px 8px', fontSize: 11, fontWeight: 600, textAlign: 'left', borderBottom: '0.5px solid var(--color-border)', color: 'var(--color-text-muted)' };
const td: React.CSSProperties = { padding: '3px 8px', fontSize: 11, borderBottom: '0.5px solid var(--color-border)' };
const mono: React.CSSProperties = { fontFamily: 'var(--font-mono, monospace)', fontSize: 11 };
const row: React.CSSProperties = { display: 'flex', gap: 8, alignItems: 'center', marginBottom: 10 };
const lab: React.CSSProperties = { fontSize: 11, color: 'var(--color-text-muted)', width: 90 };

export default function PlanFormulasGrid({ plan, canWrite }: { plan: PlanVersion; canWrite: boolean }) {
  const planId = plan.id;
  const list = useQuery(() => api.fpa.plans.formulas(planId), [planId]);
  const [edit, setEdit] = useState<PlanFormulaRow | 'new' | null>(null);

  if (list.isLoading) return <Spinner />;
  const formulas = list.data?.formulas ?? [];

  return (
    <div>
      <div style={{ display: 'flex', gap: 8, alignItems: 'center', padding: '8px 12px', flexWrap: 'wrap' }}>
        <span style={{ fontSize: 12, fontWeight: 500 }}>Formulas (custom lines)</span>
        <span style={{ fontSize: 10, color: 'var(--color-text-muted)' }}>Define a P&amp;L line as an expression over named references — e.g. <code style={mono}>revenue.total * 0.12</code>.</span>
        {canWrite && <span style={{ marginLeft: 'auto' }}><ActionButton onClick={() => setEdit('new')}>+ Add formula</ActionButton></span>}
      </div>
      <div style={{ overflowX: 'auto' }}>
        <table style={{ borderCollapse: 'collapse', minWidth: '100%' }}>
          <thead><tr><th style={th}>Line</th><th style={th}>Label</th><th style={th}>Scope</th><th style={th}>Formula</th></tr></thead>
          <tbody>
            {formulas.map((f) => (
              <tr key={f.id} style={{ cursor: canWrite ? 'pointer' : 'default' }} onClick={() => canWrite && setEdit(f)}>
                <td style={{ ...td, ...mono, fontWeight: 600 }}>{f.line_code}</td>
                <td style={td}>{f.label}</td>
                <td style={td}><Badge variant="gray">{f.scope}</Badge></td>
                <td style={{ ...td, ...mono, color: 'var(--color-text-muted)' }}>{f.expression}</td>
              </tr>
            ))}
            {formulas.length === 0 && <tr><td style={{ ...td, color: 'var(--color-text-muted)' }} colSpan={4}>No custom formulas — add one to layer a line onto the model.</td></tr>}
          </tbody>
        </table>
      </div>
      {edit && <FormulaEditor planId={planId} row={edit === 'new' ? undefined : edit}
        onClose={() => setEdit(null)}
        onSaved={() => { setEdit(null); list.refetch(); }}
        onDelete={edit !== 'new' ? async () => { const id = edit.id; setEdit(null); await api.fpa.plans.deleteFormula(planId, id); await api.fpa.plans.recompute(planId); list.refetch(); } : undefined} />}
    </div>
  );
}

function FormulaEditor({ planId, row: r, onClose, onSaved, onDelete }: {
  planId: string; row?: PlanFormulaRow; onClose: () => void; onSaved: () => void; onDelete?: () => void;
}) {
  const refs = useQuery(() => api.fpa.plans.formulaRefs(planId), [planId]);
  const [lineCode, setLineCode] = useState(r?.line_code ?? '');
  const [label, setLabel] = useState(r?.label ?? '');
  const [scope, setScope] = useState<'company' | 'department'>(r?.scope ?? 'company');
  const [expr, setExpr] = useState(r?.expression ?? '');
  const [preview, setPreview] = useState<{ months?: Array<{ period_month: string; amount: number }>; error?: string } | null>(null);
  const [previewing, setPreviewing] = useState(false);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  function insertRef(ref: string) { setExpr((s) => (s && !/[\s(]$/.test(s) ? `${s} ${ref}` : `${s}${ref}`)); setPreview(null); }
  async function runPreview() {
    if (!expr.trim()) return;
    setPreviewing(true);
    try { setPreview(await api.fpa.plans.formulaPreview(planId, expr.trim(), scope)); } finally { setPreviewing(false); }
  }
  async function save() {
    if (!lineCode.trim() || !label.trim() || !expr.trim()) { setErr('Line code, label and expression are required'); return; }
    setBusy(true); setErr(null);
    try {
      await api.fpa.plans.upsertFormula(planId, { line_code: lineCode.trim(), label: label.trim(), expression: expr.trim(), scope });
      await api.fpa.plans.recompute(planId);
      onSaved();
    } catch (e) { setErr((e as Error).message); } finally { setBusy(false); }
  }

  return (
    <Modal title={r ? 'Edit formula' : 'Add formula'} subtitle="Functions: MIN MAX SUM ABS ROUND IF PREVIOUS — PREVIOUS(x) is the prior month." onClose={onClose} width={640}>
      <div style={{ padding: 20 }}>
        {err && <p style={{ color: 'var(--color-danger, #c0392b)', fontSize: 12 }}>{err}</p>}
        <div style={row}>
          <span style={lab}>Line code</span>
          <input value={lineCode} disabled={!!r} onChange={(e) => setLineCode(e.target.value)} placeholder="cost.marketing" style={{ ...box, width: 190, opacity: r ? 0.6 : 1 }} />
          <span style={lab}>Scope</span>
          <select value={scope} onChange={(e) => { setScope(e.target.value as 'company' | 'department'); setPreview(null); }} style={box}><option value="company">company</option><option value="department">per department</option></select>
        </div>
        <div style={row}><span style={lab}>Label</span><input value={label} onChange={(e) => setLabel(e.target.value)} placeholder="Marketing" style={{ ...box, flex: 1 }} /></div>
        <div style={row}><span style={lab}>Expression</span><input value={expr} onChange={(e) => { setExpr(e.target.value); setPreview(null); }} placeholder="revenue.total * 0.12 + headcount.count * 1500" style={{ ...box, ...mono, flex: 1 }} /></div>

        <div style={{ display: 'flex', gap: 8, alignItems: 'center', marginBottom: 8, flexWrap: 'wrap' }}>
          <ActionButton onClick={runPreview} disabled={previewing || !expr.trim()}>{previewing ? 'Previewing…' : 'Preview'}</ActionButton>
          {preview?.error && <span style={{ fontSize: 11, color: 'var(--color-danger, #c0392b)' }}>{preview.error}</span>}
          {preview?.months && (
            <span style={{ fontSize: 11, color: 'var(--color-text-muted)' }}>
              {preview.months.slice(0, 6).map((m) => `${fmtMonth(m.period_month)} ${fmtCurrencyWhole(m.amount)}`).join(' · ')}{preview.months.length > 6 ? ' …' : ''}
            </span>
          )}
        </div>

        {/* Reference palette */}
        <div style={{ fontSize: 10, color: 'var(--color-text-muted)', marginBottom: 4 }}>Click to insert a reference:</div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 4, maxHeight: 180, overflowY: 'auto', paddingRight: 4 }}>
          {(refs.data?.groups ?? []).map((g) => (
            <div key={g.group} style={{ display: 'flex', gap: 4, alignItems: 'center', flexWrap: 'wrap' }}>
              <span style={{ fontSize: 10, color: 'var(--color-text-muted)', width: 90, textTransform: 'uppercase', letterSpacing: '.04em' }}>{g.group}</span>
              {g.refs.map((rf) => (
                <button key={rf.ref} onClick={() => insertRef(rf.ref)} title={rf.label}
                  style={{ ...mono, padding: '2px 6px', borderRadius: 'var(--radius-sm)', border: '0.5px solid var(--color-border-strong)', background: 'var(--color-surface)', color: 'var(--color-text)', cursor: 'pointer' }}>{rf.ref}</button>
              ))}
            </div>
          ))}
        </div>

        <div style={{ display: 'flex', gap: 8, marginTop: 16, alignItems: 'center' }}>
          <ActionButton variant="primary" onClick={save} disabled={busy}>{busy ? 'Saving…' : r ? 'Save' : 'Add'}</ActionButton>
          <ActionButton onClick={onClose}>Cancel</ActionButton>
          {onDelete && <span style={{ marginLeft: 'auto' }}><ActionButton variant="danger" onClick={onDelete}>Delete</ActionButton></span>}
        </div>
      </div>
    </Modal>
  );
}
